/**
 *
 * BillingConfiguration
 *
 */

import React, { memo, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import {
  Space,
  Text,
  // Button,
  Paper,
  Conditional,
  RadioButton,
} from '@cashfree-intl/coherent';
import { find } from 'lodash';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import Loader from 'components/Loader';
import makeSelectBillingConfiguration from './selectors';
import reducer from './reducer';
import saga from './saga';

// Constants
import {
  BILLING_OPTIONS,
  PAYLOAD_KEYS,
  APPROVED_RATE_STATUS,
  RATE_TYPE,
} from './constants';
import {
  getAllSettlementsRatesAction,
  createSettlementsRatesAction,
  enableLoaderAction,
} from './actions';
// import { getItemFromLocalStorage } from '../../utils/common';

import { MerchantContext } from '../../contexts/MerchantContext';

function getPayload(obj) {
  const result = {};
  PAYLOAD_KEYS.forEach(key => {
    if (key in obj) result[key] = obj[key];
  });
  return result;
}

export function BillingConfiguration({
  enableLoader,
  getAllSettlementsRates,
  // createSettlementsRates,
  billingConfiguration: { isLoading },
}) {
  useInjectReducer({ key: 'billingConfiguration', reducer });
  useInjectSaga({ key: 'billingConfiguration', saga });

  const { easySplitEnabled } = useContext(MerchantContext);

  const [billingConfig, setBillingConfig] = useState({});
  // const [configData, setConfigData] = useState({});

  const handleOptions = (e, { name, value }) => {
    setBillingConfig(prev => ({ ...prev, [name]: value }));
  };

  const computeConfig = response => {
    const computedBillingConfig = {};
    const computedConfigData = {};

    const { rates, settlementRates } = response;
    // computing the transaction rates config
    const config = find(rates, {
      rateType: RATE_TYPE.TRANSACTION,
      status: APPROVED_RATE_STATUS,
    });
    if (config) {
      computedBillingConfig.transaction = config.billingTo;
      computedConfigData.transaction = getPayload(config);
    }

    // computing the Standard, Instant and On Demand Settlement Charges
    settlementRates.forEach(obj => {
      if (
        obj.rateType === RATE_TYPE.SETTLEMENT &&
        obj.status === APPROVED_RATE_STATUS &&
        !computedBillingConfig.settlement
      ) {
        computedBillingConfig.settlement = obj.billingTo;
        computedConfigData.settlement = getPayload(obj);
      } else if (
        obj.rateType === RATE_TYPE.VENDOR_OD &&
        obj.status === APPROVED_RATE_STATUS &&
        !computedBillingConfig.vendorOD
      ) {
        computedBillingConfig.vendorOD = obj.billingTo;
        computedConfigData.vendorOD = getPayload(obj);
      }
    });
    // updating the state
    setBillingConfig(computedBillingConfig);
    // setConfigData(computedConfigData);
  };

  useEffect(() => {
    enableLoader();
    getAllSettlementsRates(computeConfig);
  }, []);

  // const saveChanges = () => {
  //   let transactionPayload = null;
  //   const settlementAndVendorODPayload = [];
  //   // call the create settlement rates action here
  //   Object.keys(billingConfig).forEach(key => {
  //     if (configData[key] && key.trim().toLowerCase() === 'transaction') {
  //       transactionPayload = {
  //         rateType: configData[key].rateType,
  //         adminId: getItemFromLocalStorage('merchantId'),
  //         billingTo: billingConfig[key],
  //         isPostPaid: String(configData[key].isPostPaid || false),
  //       };
  //     } else {
  //       settlementAndVendorODPayload.push({
  //         billingType: billingConfig[key],
  //         rateType: configData[key].rateType,
  //         adminId: getItemFromLocalStorage('merchantId'),
  //       });
  //     }
  //   });
  //   enableLoader();
  //   createSettlementsRates({
  //     transactionPayload,
  //     settlementAndVendorODPayload,
  //   });
  // };

  return (
    <Conditional if={easySplitEnabled}>
      <Loader active={isLoading} />
      <Space
        justifyContent="space-between"
        alignItems="center"
        className="mb-2"
      >
        {/* <Text as="span" color="bodyLight">
          Configure service charge payments. Bill them to the vendor directly,
          or you can pay the charges.
        </Text> */}
        <Text as="span" color="bodyLight">
          Please reach out to your respective Account Manager to get the
          configuration changed for your account
        </Text>
        {/* Commenting this for now - PGDB-2510 */}
        {/* <Button primary onClick={saveChanges}>
          Save Changes
        </Button> */}
      </Space>

      <Paper className="py-2 px-3 my-2">
        <Text variant="p14" className="mb-2" strong>
          Payment Gateway and Easy Split Charges
        </Text>
        <Space gap={8}>
          {BILLING_OPTIONS.map(option => (
            <RadioButton
              key={option.value}
              disabled
              name="transaction"
              onChange={handleOptions}
              checked={billingConfig.transaction === option.value}
              {...option}
            />
          ))}
        </Space>
      </Paper>

      <Paper className="py-2 px-3 my-2">
        <Text variant="p14" className="mb-2" strong>
          Standard and Instant Settlement Charges
        </Text>
        <Space gap={8}>
          {BILLING_OPTIONS.map(option => (
            <RadioButton
              key={option.value}
              disabled
              name="settlement"
              onChange={handleOptions}
              checked={billingConfig.settlement === option.value}
              {...option}
            />
          ))}
        </Space>
      </Paper>

      <Conditional if={billingConfig.vendorOD}>
        <Paper className="py-2 px-3 my-2">
          <Text variant="p14" className="mb-2" strong>
            On-Demand Settlement Charges
          </Text>
          <Space gap={8}>
            {BILLING_OPTIONS.map(option => (
              <RadioButton
                key={option.value}
                disabled
                name="vendorOD"
                onChange={handleOptions}
                checked={billingConfig.vendorOD === option.value}
                {...option}
              />
            ))}
          </Space>
        </Paper>
      </Conditional>
    </Conditional>
  );
}

BillingConfiguration.propTypes = {
  enableLoader: PropTypes.func.isRequired,
  getAllSettlementsRates: PropTypes.func.isRequired,
  createSettlementsRates: PropTypes.func.isRequired,
  billingConfiguration: PropTypes.object.isRequired,
};

const mapStateToProps = createStructuredSelector({
  billingConfiguration: makeSelectBillingConfiguration(),
});

function mapDispatchToProps(dispatch) {
  return {
    enableLoader: () => dispatch(enableLoaderAction()),
    getAllSettlementsRates: data =>
      dispatch(getAllSettlementsRatesAction(data)),
    createSettlementsRates: data =>
      dispatch(createSettlementsRatesAction(data)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(BillingConfiguration);
